import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { CustomFormComponent } from '@bo-client/activation-management/custom-form.component';
import { DataForCustomFormModel } from '@bo-client/activation-management/shared/models/actionWithMoreInfo.model';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CommissioningRequestDtoModel } from '@bo-client/activation-management/shared/models/commissioningRequestDto';
import { CreateFormsCommissioningService } from '@bo-client/activation-management/commissioning/forms/create-forms-commissioning.service';
import { AreaTypeEnum } from '@bo-client/activation-management/shared/models/commissioningModel';
import { uri } from '@bo-client/shared/constants/uri';
import { from, Observable, of, toArray } from 'rxjs';
import { GridRegistrationModel } from '@bo-client/activation-management/grid-registration/model/grid-registration.model';
import {
  placeInServiceProtocolRequired,
  requiredStateForCommissioningCompletion,
  requiredStateForCommissioningCompletionLabel
} from '@bo-client/activation-management/grid-registration/grid-registration-process.util';
import { HttpHapService } from '@bo-client/core/http/http-hap.service';
import { DataShareService } from '@bo-client/shared/services/data-share.service';
import { filter, map, mergeMap, shareReplay } from 'rxjs/operators';

@Component({
  selector: 'app-cr-completition-form',
  templateUrl: './cr-completition-form.component.html',
  styleUrls: ['./cr-completition-form.component.scss']
})
export class CrCompletitionFormComponent
  implements CustomFormComponent<CommissioningRequestDtoModel, CommissioningRequestDtoModel>, OnInit
{
  private readonly httpHapService = inject(HttpHapService);
  private readonly dataShareService = inject(DataShareService);

  @Input() data: DataForCustomFormModel<CommissioningRequestDtoModel>;
  @Output() onConfirm: EventEmitter<CommissioningRequestDtoModel> = new EventEmitter();

  gridRegistrationErrorMessage$: Observable<string | null>;
  public model: CommissioningRequestDtoModel;
  public currentForm: FormGroup;
  get connectionPowerUnit(): string {
    switch (this.model.area.name) {
      case AreaTypeEnum.POWER:
        return 'kVa';
      case AreaTypeEnum.WATER:
        return 'm³/h';
      default:
        return 'kVa';
    }
  }

  get actionFormGroups() {
    return this.currentForm?.get('actions') as FormArray;
  }

  constructor(private fb: FormBuilder, private formService: CreateFormsCommissioningService) {}

  ngOnInit(): void {
    this.model = this.data.model;

    this.gridRegistrationErrorMessage$ = this.getRelatedGrAndCheckErrors$(this.model);

    this.currentForm = this.fb.group({
      actions: this.fb.array(
        this.data.model.actions.map((data) =>
          this.formService.createActionsForm(data, <AreaTypeEnum>this.model.area.name, 'CompletionForm')
        )
      ),
      supplyAppointment: [this.model.supplyAppointment, [Validators.required]],
      comment: []
    });
    //this.currentForm = this.createFormsGridRegistrationService.createFeedinManagementConfigurationForm(
    //  this.data.model
    //);
    /**
     * für jede aktion mit einem Zähler?
     * => Zä
     */
  }

  cancel() {
    this.onConfirm.emit(null);
  }

  confirm() {
    if (this.currentForm.valid) {
      this.model.actions = (this.currentForm.get('actions') as FormArray).value;
      this.model.supplyAppointment = this.currentForm.get('supplyAppointment').value;

      this.onConfirm.emit(this.model);
    } else {
      this.currentForm.markAllAsTouched();
    }
  }

  getRelatedGrAndCheckErrors$(model: CommissioningRequestDtoModel): Observable<string | null> {
    if (!model || model.area.name !== AreaTypeEnum.POWER || !model.actions?.length) {
      return of(null);
    }

    const checkMastrDataOnCommissioningCompletion = this.dataShareService.uiAdaptions.find(
      (y) => y.anchor === 'commissioining.checkMastrDataOnCommissioningCompletition'
    );

    return from(model.actions).pipe(
      mergeMap((action) => from(action.devices)),
      map((dev) => dev.gridRegistration?.id),
      filter((id): id is number => !!id),
      mergeMap((id) =>
        this.httpHapService.getTyped<GridRegistrationModel>(`${uri.cmGridRegistration}/${id}`)
      ),
      toArray(),
      map((resArr: GridRegistrationModel[]) => {
        let error: string = '';

        resArr.forEach((gridRegistration) => {
          // Einspeiseanlagen und v2-Speicher mit Einspeisung durchlaufen vorher ein
          // Inbetriebsetzungsprotokoll; alle übrigen Netzanmeldungen nicht.
          const needsPlaceInServiceProtocol = placeInServiceProtocolRequired(gridRegistration);

          if (
            needsPlaceInServiceProtocol &&
            !(
              gridRegistration.placeInService &&
              Object.values(gridRegistration.placeInService).some(
                (value) => value !== null && value !== undefined
              )
            )
          ) {
            error =
              (error ?? '') +
              `Für die verknüpfte Netzanmeldung mit ID ${gridRegistration.id} muss zusätzlich ein Inbetriebsetzungsprotokoll vorliegen.` +
              '</br>';
          }

          if (
            checkMastrDataOnCommissioningCompletion?.value === 'true' &&
            !gridRegistration.maStRRegistrationDate &&
            needsPlaceInServiceProtocol
          ) {
            error =
              (error ?? '') +
              `Für die verknüpfte Netzanmeldung mit ID ${gridRegistration.id} muss die Marktstammregisternummern eingetragen sein.` +
              '</br>';
          }

          if (gridRegistration.state !== requiredStateForCommissioningCompletion(gridRegistration)) {
            error =
              error +
              `Die verknüpfte Netzanmeldung mit ID ${gridRegistration.id} ` +
              `${requiredStateForCommissioningCompletionLabel(gridRegistration)}.` +
              '</br>';
          }
        });

        if (error) {
          return 'Sie können diesen Inbetriebnahmeantrag nicht abschließen.' + '</br>' + error;
        }

        return null;
      }),
      shareReplay(1)
    );
  }
}
